import React, { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/Header";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { isMetaInAppBrowser } from "@/lib/inAppBrowser";
import {
  rememberAuthRedirect,
  trackAuthRedirectReturned,
  trackFunnelEvent,
} from "@/lib/funnelEvents";

/** ===== Paleta ===== */
const COLORS = {
  primary: "#FE5668",
  primaryLight: "#FF8D8F",
  accent: "#FEC1A5",
  successLight: "#B9D394",
  success: "#64A002",
};

type ApiResponse<T> = T | { item?: T; data?: T; servico?: T };

type HostBlock = {
  nome?: string;
  bio?: string;
  endereco?: string | null;
  aceita?: string[];
  porte?: string[];
  adicionais?: string[];
};

type VagasInfo = {
  limite_pets?: number;
  vagas_disponiveis?: number;
  esta_disponivel?: boolean;
};

type ServiceDetailData = {
  servico_id?: number;
  tipo_servico?: any;
  anfitriao?: any;
  imagens?: string[];
  host?: HostBlock;
  vagas_info?: VagasInfo;
  servico?: any;
  tempo_resposta?: string;
  titulo?: string;
  nome?: string;
  [key: string]: any;
};

type ChatMsg = {
  id?: number;
  conversa_id?: number;
  remetente_id?: number;
  mensagem: string;
  created_at: string;
};

type HorarioSlot = {
  hora_inicio: string;
  hora_fim: string;
};

type ModalidadeBanhoTosa = "banho" | "tosa" | "banho_e_tosa";
type PackageValidityUnit = "day" | "week" | "month";
type ServicePackage = {
  id: string;
  name: string;
  units: number;
  price: number;
  validity_quantity: number;
  validity_unit: PackageValidityUnit;
  validity_days?: number;
  variant_key?: ModalidadeBanhoTosa | null;
};

const MODALIDADES_BANHO_TOSA: Array<{ key: ModalidadeBanhoTosa; label: string }> = [
  { key: "banho", label: "Só banho" },
  { key: "tosa", label: "Só tosa" },
  { key: "banho_e_tosa", label: "Banho e tosa" },
];

/** ===== Helpers ===== */
function parseEntradaDate(entrada: string | null): Date {
  if (!entrada) return new Date();

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(entrada)) {
    const [d, m, y] = entrada.split("/");
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(entrada)) {
    const [y, m, d] = entrada.split("-");
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  return new Date(entrada);
}

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseHoraInicio(raw: any): { h: number; m: number } {
  const hora = (raw ?? "23:59").toString().replace(/[^0-9:]/g, "");
  const parts = hora.split(":");
  if (parts.length < 2) return { h: 23, m: 59 };

  const h = Number(parts[0]);
  const m = Number(parts[1]);

  return {
    h: Number.isFinite(h) ? h : 23,
    m: Number.isFinite(m) ? m : 59,
  };
}

function diffDays(entrada: string | null, saida: string | null): number {
  if (!entrada || !saida) return 1;

  const start = parseEntradaDate(entrada);
  const end = parseEntradaDate(saida);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const ms = end.getTime() - start.getTime();
  const days = Math.round(ms / 86400000);

  return days > 0 ? days : 1;
}

function diffHours(horaInicio: string | null, horaFim: string | null): number {
  if (!horaInicio || !horaFim) return 1;

  const [hi, mi] = horaInicio.split(":").map(Number);
  const [hf, mf] = horaFim.split(":").map(Number);

  if (
    !Number.isFinite(hi) ||
    !Number.isFinite(mi) ||
    !Number.isFinite(hf) ||
    !Number.isFinite(mf)
  ) {
    return 1;
  }

  const inicioMin = hi * 60 + mi;
  const fimMin = hf * 60 + mf;
  const diff = fimMin - inicioMin;

  return diff > 0 ? diff / 60 : 1;
}

function addMinutesToHHmm(hhmm: string, minutesToAdd: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  const total = h * 60 + m + minutesToAdd;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function isToday(d: Date): boolean {
  const now = new Date();
  return d.toDateString() === now.toDateString();
}

function addDays(d: Date, days: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}

function setTime(d: Date, h: number, m: number): Date {
  const x = new Date(d);
  x.setHours(h, m, 0, 0);
  return x;
}

function formatDateTimeBR(d?: Date | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function formatDateBR(value: any): string {
  if (!value) return "—";
  if (typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR").format(d);
}

function formatHHmm(d?: Date | null): string {
  if (!d) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function formatDDMM(d?: Date | null): string {
  if (!d) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  }).format(d);
}

function getTutorName(a: any): string {
  return a?.tutor_nome ?? a?.tutor?.name ?? a?.nome ?? "Tutor";
}

function safeComment(a: any): string {
  const c = (a?.comentario ?? "").toString().trim();
  return c.length ? c : "Sem comentário.";
}

function toNumber(v: any): number | undefined {
  if (v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function isBanhoTosaNome(nome: string): boolean {
  const normalizado = nome.toLowerCase();
  return normalizado.includes("banho") && normalizado.includes("tosa");
}

function brl(v?: number): string {
  if (v == null) return "—";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function packageValidityLabel(pkg: ServicePackage): string {
  const amount = Math.max(1, Number(pkg.validity_quantity || 1));
  const unit = pkg.validity_unit || "month";
  const labels: Record<PackageValidityUnit, [string, string]> = {
    day: ["dia", "dias"],
    week: ["semana", "semanas"],
    month: ["mês", "meses"],
  };
  return `${amount} ${labels[unit][amount === 1 ? 0 : 1]}`;
}

function bathPackageLabel(variant?: ModalidadeBanhoTosa | null): string | null {
  return MODALIDADES_BANHO_TOSA.find((option) => option.key === variant)?.label ?? null;
}

function formatHoraCurta(hora?: string | null): string {
  if (!hora) return "—";
  return String(hora).slice(0, 5);
}

function parseBool(value: any, fallback = true): boolean {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  const normalizado = String(value).trim().toLowerCase();
  if (["1", "true", "sim", "yes"].includes(normalizado)) return true;
  if (["0", "false", "nao", "não", "no"].includes(normalizado)) return false;

  return fallback;
}

function pickDetail(payload: any): ServiceDetailData | null {
  if (!payload) return null;
  if (payload.servico_id || payload.imagens || payload.vagas_info || payload.host) return payload;
  if (payload.item) return payload.item;
  if (payload.data) return payload.data;
  if (payload.servico) return payload.servico;
  return payload;
}

function RatingStars({ value }: { value?: number | null }) {
  const nota = Math.max(0, Math.min(5, Math.round(value ?? 0)));

  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`Nota ${value ?? 0} de 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= nota ? "text-[#d4af37]" : "text-gray-300"}>
          ★
        </span>
      ))}
    </span>
  );
}

function getIconForService(service: string): string {
  const s = service.toLowerCase();
  if (s.includes("câmera") || s.includes("camera") || s.includes("video")) return "📹";
  if (s.includes("quintal") || s.includes("jardim")) return "🏡";
  if (s.includes("passeio")) return "🦮";
  if (s.includes("medicamento") || s.includes("remédio") || s.includes("remedio")) return "💊";
  if (s.includes("banho")) return "🚿";
  if (s.includes("adestramento")) return "🎓";
  return "✅";
}

function parsePolicyKey(label: string): "livre" | "intermediaria" | "fixa" {
  if (label.toLowerCase().includes("livre")) return "livre";
  if (label.toLowerCase().includes("fixa")) return "fixa";
  return "intermediaria";
}

function horarioJaPassou(dataSelecionada: string | null, horaInicio: string) {
  if (!dataSelecionada) return false;

  const agora = new Date();

  const [ano, mes, dia] = dataSelecionada.split("-").map(Number);
  const [hora, minuto] = horaInicio.split(":").map(Number);

  const dataHorario = new Date(ano, mes - 1, dia, hora, minuto || 0, 0);

  return dataHorario <= agora;
}

function computeCancellationWindow(args: {
  policyKey: "livre" | "intermediaria" | "fixa";
  entradaParam: string | null;
  horaInicioRaw: any;
}) {
  const now = new Date();
  const dataInicio = parseEntradaDate(args.entradaParam);
  const { h, m } = parseHoraInicio(args.horaInicioRaw);
  const inicio = setTime(dataInicio, h, m);

  let limite100: Date | null = null;
  if (args.policyKey === "livre") limite100 = new Date(inicio);
  if (args.policyKey === "intermediaria") limite100 = setTime(addDays(inicio, -3), 12, 0);
  if (args.policyKey === "fixa") limite100 = setTime(addDays(inicio, -7), 12, 0);

  const limite100Label = limite100
    ? limite100.getTime() > now.getTime()
      ? formatDateTimeBR(limite100)
      : "já encerrado para esta data"
    : "—";

  const hoje = isToday(inicio);

  const short = hoje
    ? args.policyKey === "livre"
      ? `Hoje: 100% até ${formatHHmm(inicio)}. Após o início, não é possível cancelar.`
      : limite100 && limite100.getTime() > now.getTime()
      ? `Hoje: 100% até ${formatHHmm(limite100)}; depois, 50% até ${formatHHmm(inicio)}.`
      : `Hoje: 50% até ${formatHHmm(inicio)}.`
    : args.policyKey === "livre"
    ? "100% até o início do serviço."
    : `100% até ${formatDDMM(limite100)} às 12h; depois, 50% até o início.`;

  const bullets =
    args.policyKey === "livre"
      ? [
          "100% de reembolso se cancelar antes do início do serviço.",
          "Depois disso (até o início): 50%.",
        ]
      : args.policyKey === "intermediaria"
      ? [
          "100% de reembolso até 3 dias antes do início (12h).",
          "Menos de 3 dias (até o início): 50%.",
        ]
      : [
          "100% de reembolso até 7 dias antes do início (12h).",
          "Menos de 7 dias (até o início): 50%.",
        ];

  return { inicio, limite100, limite100Label, short, bullets };
}

function diaKeyFromISO(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function labelSeparador(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const ontem = new Date(hoje);
  ontem.setDate(ontem.getDate() - 1);

  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);

  if (dd.getTime() === hoje.getTime()) return "Hoje";
  if (dd.getTime() === ontem.getTime()) return "Ontem";

  return dd.toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function horaHHmm(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** ===== Componentes auxiliares ===== */
function CancellationPopoverContent({
  bullets,
  inicio,
  limite100Label,
}: {
  bullets: string[];
  inicio: Date;
  limite100Label: string;
}) {

  
  return (
    <div className="space-y-3 text-sm">
      <Header />
      <ul className="list-disc pl-5 space-y-1">
        {bullets.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>

      <hr className="my-2" />

      <div className="text-gray-600">
        <strong>Para esta reserva</strong>
        <br />
        Início: {formatDateTimeBR(inicio)}
        <br />
        100% até: {limite100Label}
        <br />
        Após o início: <strong>não é possível cancelar</strong>.
      </div>
    </div>
  );
}

function HoverPopover({
  triggerText = "Como funciona?",
  content,
}: {
  triggerText?: string;
  content: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-popover-root]")) setOpen(false);
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <span
      className="relative inline-flex"
      data-popover-root
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-600 text-white hover:bg-gray-700"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {triggerText}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Como funciona a política de cancelamento"
          className="absolute left-0 top-full mt-2 w-[340px] rounded-xl border bg-white shadow-xl p-4 text-sm text-gray-700 z-50"
        >
          <div className="absolute -top-2 left-6 w-4 h-4 bg-white border-l border-t rotate-45" />
          {content}
        </div>
      )}
    </span>
  );
}

function DetailSections({
  descricaoServico,
  adicionaisBadges,
  policyBadgeLabel,
  cancellationText,
  media,
  avals,
  onOpenReviews,
}: {
  descricaoServico: string;
  adicionaisBadges: string[];
  policyBadgeLabel: string;
  cancellationText: string;
  media: number | null;
  avals: any[];
  onOpenReviews: () => void;
}) {
  return (
    <>
      <section className="border-b pb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Sobre este serviço</h2>
        <p className="text-gray-600 leading-relaxed whitespace-pre-line">{descricaoServico}</p>
      </section>

      <section className="border-b pb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">O que este lugar oferece</h2>

        {adicionaisBadges.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {adicionaisBadges.map((item, idx) => (
              <div key={`${item}-${idx}`} className="flex gap-3 items-start">
                <span className="text-xl bg-gray-50 p-2 rounded-lg">{getIconForService(item)}</span>
                <div>
                  <div className="font-semibold">{item}</div>
                  <div className="text-sm text-gray-500">
                    Oferecido pelo anfitrião para garantir o conforto do seu pet.
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500">Sem serviços adicionais informados.</div>
        )}
      </section>

      <section className="border-b pb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Política de cancelamento</h2>

        <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold mb-3 bg-gray-50 text-gray-800">
          {policyBadgeLabel}
        </div>

        <p className="text-gray-600 leading-relaxed whitespace-pre-line">{cancellationText}</p>
      </section>

      <section className="border-b pb-8">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl font-semibold text-gray-800">
            Avaliações
          </h2>
          <button
            type="button"
            onClick={onOpenReviews}
            className="inline-flex w-fit items-center gap-2 rounded-full border border-[#d4af37]/30 bg-[#fff8df] px-3 py-1.5 text-sm font-semibold text-gray-900 transition hover:bg-[#fff2bd]"
          >
            <RatingStars value={media ?? 0} />
            <span>{media != null ? media.toFixed(1) : "Novo"}</span>
            <span className="text-gray-500">· {avals.length} avaliação{avals.length === 1 ? "" : "es"}</span>
          </button>
        </div>

        {avals.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {avals.slice(0, 2).map((a: any) => (
              <div
                key={a.id ?? `${a.user_id}-${a.created_at}`}
                className="p-4 border rounded-xl shadow-sm"
              >
                <div className="font-bold text-sm mb-2">
                  <RatingStars value={Number(a?.nota ?? 0)} />
                </div>
                <p className="mb-2 text-xs font-semibold text-gray-700">
                  {getTutorName(a)}
                </p>
                <p className="text-sm text-gray-600 italic line-clamp-3">
                  "{a?.comentario ? a.comentario : "Sem comentário."}"
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500">Ainda não há avaliações.</div>
        )}
      </section>
    </>
  );
}

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const csrf =
    (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || "";

  
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [raw, setRaw] = useState<ServiceDetailData | null>(null);

  const [openReviews, setOpenReviews] = useState(false);
  const [openReserva, setOpenReserva] = useState(false);

  const [watchingNow, setWatchingNow] = useState<number | null>(null);
  const [demandBadge, setDemandBadge] = useState<string | null>(null);

  const [meusPets, setMeusPets] = useState<any[]>([]);
  const [petsLoading, setPetsLoading] = useState(false);
  const [petsError, setPetsError] = useState<string | null>(null);
  const [petIdsSelecionados, setPetIdsSelecionados] = useState<number[]>([]);
  const [pagamentoEnviando, setPagamentoEnviando] = useState(false);
  const [petRequiredNotice, setPetRequiredNotice] = useState(false);

  const [horarioSelecionado, setHorarioSelecionado] = useState<HorarioSlot | null>(null);
  const [reservaEntrada, setReservaEntrada] = useState("");
  const [reservaSaida, setReservaSaida] = useState("");
  const [modalidadeServico, setModalidadeServico] = useState<ModalidadeBanhoTosa | "">("");
  const [selectedPackageId, setSelectedPackageId] = useState("");

  const [chatOpen, setChatOpen] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatConversaId, setChatConversaId] = useState<number | null>(null);
  const inAppBrowser = isMetaInAppBrowser();
  const [chatAnfitriaoId, setChatAnfitriaoId] = useState<number | null>(null);
  const [chatAnfitriaoNome, setChatAnfitriaoNome] = useState<string>("Chat");
  const [chatMensagens, setChatMensagens] = useState<ChatMsg[]>([]);
  const [chatTexto, setChatTexto] = useState("");

  const userId = (window as any).__AUTH_ID__ ?? null;
  
  const echo = (window as any).Echo;

  useEffect(() => {
    trackAuthRedirectReturned({
      page: "service_detail",
      authenticated: Boolean(userId),
    });
  }, [userId]);

  useEffect(() => {
    return () => {
      try {
        (echo as any)?.disconnect?.();
      } catch {}
    };
  }, [echo]);

  const entradaParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("entrada");
  }, [location.search]);

  const saidaParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("saida");
  }, [location.search]);

  const horaInicioParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("hora_inicio");
  }, [location.search]);

  const horaFimParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("hora_fim");
  }, [location.search]);

  const modalidadeParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const modalidade = params.get("modalidade");

    if (
      modalidade === "banho" ||
      modalidade === "tosa" ||
      modalidade === "banho_e_tosa"
    ) {
      return modalidade as ModalidadeBanhoTosa;
    }

    return "";
  }, [location.search]);

  useEffect(() => {
    setReservaEntrada(entradaParam ?? "");
    setReservaSaida(saidaParam ?? entradaParam ?? "");
  }, [entradaParam, saidaParam]);

  useEffect(() => {
    setModalidadeServico(modalidadeParam);
  }, [modalidadeParam]);

  const entradaSelecionada = reservaEntrada || entradaParam || "";
  const saidaSelecionada = reservaSaida || saidaParam || entradaSelecionada;
  const temDataSelecionada = Boolean(entradaSelecionada);
  const hojeYmd = useMemo(() => toYmd(new Date()), []);
  const acessoDiretoSemData = !entradaParam && !saidaParam;
  const currentUrl = `${location.pathname}${location.search}`;
  const loginUrl = `/login?redirect=${encodeURIComponent(currentUrl)}`;
  const cadastroUrl = `/register?redirect=${encodeURIComponent(currentUrl)}`;
  const cadastrarPetUrl = `/pets/novo?redirect=${encodeURIComponent(currentUrl)}`;
  const mostrarAvisoCadastro = !userId;

  const endpoint = useMemo(() => {
    const params = new URLSearchParams();

    if (entradaSelecionada) params.set("entrada", entradaSelecionada);
    if (saidaSelecionada) params.set("saida", saidaSelecionada);
    if (horaInicioParam) params.set("hora_inicio", horaInicioParam);
    if (horaFimParam) params.set("hora_fim", horaFimParam);

    const qs = params.toString();

    return qs ? `/api/servico/${id}?${qs}` : `/api/servico/${id}`;
  }, [id, entradaSelecionada, saidaSelecionada, horaInicioParam, horaFimParam]);

  useEffect(() => {
    let ativo = true;

    async function carregarMeusPets() {
      if (!csrf) {
        setMeusPets([]);
        return;
      }

      try {
        setPetsLoading(true);
        setPetsError(null);

        const res = await fetch("/tutor/pets", {
          credentials: "include",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        if (res.status === 401) {
          if (ativo) setMeusPets([]);
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        const pets = Array.isArray(json?.pets) ? json.pets : [];

        if (ativo) setMeusPets(pets);
      } catch (e: any) {
        if (!ativo) return;
        setPetsError(e?.message ?? "Falha ao carregar pets");
        setMeusPets([]);
      } finally {
        if (ativo) setPetsLoading(false);
      }
    }

    carregarMeusPets();

    return () => {
      ativo = false;
    };
  }, [csrf]);

  useEffect(() => {
    let alive = true;

    const roll = Math.random();
    if (roll < 0.25) setDemandBadge("🔥 Em alta hoje");
    else if (roll < 0.4) setDemandBadge("✅ Reserva rápida");
    else setDemandBadge(null);

    const base = 2 + Math.floor(Math.random() * 4);
    setWatchingNow(base);

    const t = setInterval(() => {
      setWatchingNow((prev) => {
        if (prev == null) return prev;
        const delta = Math.random() < 0.5 ? -1 : 1;
        return Math.max(1, Math.min(7, prev + delta));
      });
    }, 7000);

    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const res = await fetch(endpoint, { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json: ApiResponse<ServiceDetailData> = await res.json();
        const item = pickDetail(json);

        if (!alive) return;
        setRaw(item);
      } catch (e: any) {
        if (!alive) return;
        setErr(e?.message ?? "Falha ao carregar");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [endpoint]);

  const data = raw ?? {};
  const servico = data.servico ?? data;
  const anfitriao = data.anfitriao ?? servico?.anfitriao ?? null;
  const host: HostBlock | null = data.host ?? null;
  const hp = anfitriao?.host_profile ?? null;
  const hra = anfitriao?.host_request_aprovado ?? null;

  const title =
    data.titulo ??
    data.nome ??
    data?.tipo_servico?.nome ??
    servico?.tipo_servico?.nome ??
    `Serviço #${data.servico_id ?? id ?? ""}`;

  const tipoServicoNome = data?.tipo_servico?.nome ?? servico?.tipo_servico?.nome ?? "—";

  const descricaoServico =
    data?.descricao ??
    data?.tipo_servico?.descricao ??
    servico?.tipo_servico?.descricao ??
    "Sem descrição disponível.";

  const bio = host?.bio ?? hp?.bio ?? "Sem descrição.";

  const endereco = useMemo(() => {
    const parts = [hra?.endereco, hra?.bairro, hra?.cidade, hra?.estado]
      .filter(Boolean)
      .join(", ");
    return parts || host?.endereco || "Não informado";
  }, [hra, host?.endereco]);

  const limitePets = toNumber(data?.vagas_info?.limite_pets);

  const tipoPrecificacao =
    data?.tipo_servico?.tipo_precificacao ??
    servico?.tipo_servico?.tipo_precificacao ??
    "diaria";

  const tipoServicoSlug = (
    data?.tipo_servico?.slug ??
    servico?.tipo_servico?.slug ??
    ""
  )
    .toString()
    .toLowerCase();

  const exigeModalidadeBanhoTosa =
    Boolean(data?.exige_modalidade ?? servico?.exige_modalidade) ||
    isBanhoTosaNome(`${tipoServicoNome} ${tipoServicoSlug}`);

  const precosModalidadeBanhoTosa = useMemo(() => {
    const origem =
      (data as any)?.modalidades_servico ??
      (servico as any)?.modalidades_servico ??
      {};

    return {
      banho: toNumber(origem?.banho ?? (servico as any)?.preco_banho),
      tosa: toNumber(origem?.tosa ?? (servico as any)?.preco_tosa),
      banho_e_tosa: toNumber(origem?.banho_e_tosa ?? (servico as any)?.preco_banho_tosa),
    } satisfies Record<ModalidadeBanhoTosa, number | undefined>;
  }, [data, servico]);

  const modalidadesDisponiveisBanhoTosa = useMemo(
    () => MODALIDADES_BANHO_TOSA.filter(({ key }) => Number(precosModalidadeBanhoTosa[key] || 0) > 0),
    [precosModalidadeBanhoTosa]
  );

  const menorPrecoModalidadeBanhoTosa = useMemo(() => {
    const valores = Object.values(precosModalidadeBanhoTosa).filter(
      (valor): valor is number => typeof valor === "number" && valor > 0
    );

    return valores.length ? Math.min(...valores) : undefined;
  }, [precosModalidadeBanhoTosa]);

  const precoModalidadeSelecionada =
    modalidadeServico ? precosModalidadeBanhoTosa[modalidadeServico] : undefined;

  const servicePackages = useMemo<ServicePackage[]>(() => {
    const source = Array.isArray((data as any)?.packages)
      ? (data as any).packages
      : Array.isArray((servico as any)?.pricing_config?.packages)
      ? (servico as any).pricing_config.packages
      : [];

    return source
      .map((pkg: any) => ({
        id: String(pkg?.id || ""),
        name: String(pkg?.name || ""),
        units: Math.max(1, Number(pkg?.units || 1)),
        price: Math.max(0, Number(pkg?.price || 0)),
        validity_quantity: Math.max(1, Number(pkg?.validity_quantity || 1)),
        validity_unit: (["day", "week", "month"].includes(String(pkg?.validity_unit))
          ? String(pkg.validity_unit)
          : "month") as PackageValidityUnit,
        validity_days: Number(pkg?.validity_days || 30),
        variant_key: ["banho", "tosa", "banho_e_tosa"].includes(String(pkg?.variant_key))
          ? pkg.variant_key as ModalidadeBanhoTosa
          : null,
      }))
      .filter((pkg: ServicePackage) => pkg.id !== "" && pkg.price > 0)
      .filter((pkg: ServicePackage) => !pkg.variant_key || Number(precosModalidadeBanhoTosa[pkg.variant_key] || 0) > 0);
  }, [data, servico, precosModalidadeBanhoTosa]);

  const selectedPackage = servicePackages.find((pkg) => pkg.id === selectedPackageId) ?? null;

  const isHoraria =
    tipoPrecificacao === "horaria" ||
    ["petsitter", "passeio", "veterinario"].includes(tipoServicoSlug);

  const isPeriodo =
    tipoPrecificacao === "diaria" ||
    tipoPrecificacao === "creche" ||
    tipoServicoSlug === "creche";

  const vagasDisp = toNumber(data?.vagas_info?.vagas_disponiveis);
  const mostrarBlocoVagas = !isHoraria && vagasDisp != null;
  const estaLotado = vagasDisp != null && vagasDisp <= 0;
  const ultimaVaga = vagasDisp === 1;

  const checkinPadrao = formatHoraCurta(
    data?.checkin_padrao ??
      servico?.checkin_padrao ??
      (tipoPrecificacao === "creche" ? "07:00" : "12:00")
  );
  const checkoutPadrao = formatHoraCurta(
    data?.checkout_padrao ??
      servico?.checkout_padrao ??
      (tipoPrecificacao === "creche" ? "19:00" : "12:00")
  );
  const toleranciaCheckout = toNumber(
    data?.tolerancia_checkout_minutos ?? servico?.tolerancia_checkout_minutos
  ) ?? (tipoPrecificacao === "creche" ? 30 : 0);
  const lateCheckoutLimite = formatHoraCurta(
    data?.late_checkout_limite ??
      servico?.late_checkout_limite ??
      (tipoPrecificacao === "creche" ? "21:00" : "18:00")
  );
  const cobrarAtrasoCheckout = parseBool(
    data?.cobrar_atraso_checkout ?? servico?.cobrar_atraso_checkout,
    true
  );

  const resumoCheckout = useMemo(() => {
    if (!isPeriodo) return null;

    if (!cobrarAtrasoCheckout) {
      return "Atrasos no checkout não geram cobrança adicional para esta reserva.";
    }

    if (tipoPrecificacao === "diaria") {
      return `Saídas após ${checkoutPadrao} podem gerar cobrança adicional. Até ${lateCheckoutLimite}, pode ser cobrada 1 diária de creche. Após ${lateCheckoutLimite}, pode ser cobrada mais 1 diária de hospedagem.`;
    }

    if (tipoPrecificacao === "creche") {
      const trechoTolerancia =
        toleranciaCheckout > 0
          ? `Há tolerância de ${toleranciaCheckout} min após ${checkoutPadrao}. `
          : "";

      return `${trechoTolerancia}Depois disso, o anfitrião pode cobrar atraso. Após ${lateCheckoutLimite}, pode ser cobrada uma nova diária de creche.`;
    }

    return null;
  }, [
    cobrarAtrasoCheckout,
    isPeriodo,
    lateCheckoutLimite,
    checkoutPadrao,
    tipoPrecificacao,
    toleranciaCheckout,
  ]);

  const urgencyText = estaLotado
    ? "🔴 Sem vagas no período"
    : ultimaVaga
    ? "⚠️ Última vaga disponível"
    : demandBadge;

  const showUrgency = Boolean(urgencyText);

  const avaliacoesResumo = (data as any)?.avaliacoes ?? null;
  const avals = Array.isArray(avaliacoesResumo?.historico)
    ? avaliacoesResumo.historico
    : Array.isArray(anfitriao?.avaliacoes)
    ? anfitriao.avaliacoes
    : [];
  const mediaCalculada = avals.length
    ? avals.reduce((acc: number, a: any) => acc + (Number(a?.nota) || 0), 0) / avals.length
    : null;
  const media = avaliacoesResumo?.total > 0
    ? Number(avaliacoesResumo.media || 0)
    : mediaCalculada;
  const totalAvaliacoes = Number(avaliacoesResumo?.total ?? avals.length ?? 0);

  const aceitaBadges: string[] = useMemo(() => {
    if (Array.isArray(host?.aceita) && host.aceita.length) return host.aceita;

    const out: string[] = [];
    if (hp?.aceita_caes) out.push("cachorros 🐶");
    if (hp?.aceita_gatos) out.push("gatos 🐱");
    if (hp?.aceita_outros) out.push("outros 🐰");
    if (hp?.aceita_filhotes) out.push("filhotes 🍼");
    if (hp?.aceita_idosos) out.push("idosos 👴");
    if (hp?.aceita_necessidades_especiais) out.push("especiais ♿");
    return out;
  }, [host?.aceita, hp]);

  const porteBadges: string[] = useMemo(() => {
    if (Array.isArray(host?.porte) && host.porte.length) return host.porte;

    const out: string[] = [];
    if (hp?.aceita_ate_10kg) out.push("Pequeno até 10kg");
    if (hp?.aceita_ate_25kg) out.push("Médio até 25kg");
    if (hp?.aceita_ate_40kg) out.push("Grande até 40kg");
    if (hp?.aceita_acima_40kg) out.push("Gigante acima de 40kg");
    return out;
  }, [host?.porte, hp]);

  const adicionaisBadges: string[] = useMemo(() => {
    if (Array.isArray(host?.adicionais) && host.adicionais.length) return host.adicionais;

    const out: string[] = [];
    if (hp?.inclui_banho) out.push("Banho");
    if (hp?.inclui_passeio) out.push("Passeio");
    if (hp?.administra_medicacao) out.push("Administro medicação");
    if (hp?.oferece_transporte) out.push("Transporte");
    return out;
  }, [host?.adicionais, hp]);

  const policyRaw = (servico?.cancellation_policy ?? "").toString().toLowerCase();

  const policyBadgeLabel =
    policyRaw.includes("liv") || policyRaw.includes("flex")
      ? "🟢 Livre"
      : policyRaw.includes("inter") || policyRaw.includes("mod")
      ? "🟡 Intermediária"
      : policyRaw.includes("fix") || policyRaw.includes("rig")
      ? "🔴 Fixa"
      : "🟡 Intermediária";

  const policyKey = parsePolicyKey(policyBadgeLabel);

  const cancelWin = useMemo(
    () =>
      computeCancellationWindow({
        policyKey,
        entradaParam: entradaSelecionada || null,
        horaInicioRaw: servico?.hora_inicio ?? data?.hora_inicio ?? "23:59",
      }),
    [policyKey, entradaSelecionada, servico?.hora_inicio, data?.hora_inicio]
  );

  const policyShort = cancelWin.short;

  const cancellationText =
    data?.politica_cancelamento ??
    servico?.cancellation_policy ??
    "Política padrão da plataforma.";

  const precoPadrao =
    toNumber((servico as any)?.preco_base) ??
    toNumber((data as any)?.preco_base) ??
    toNumber((data as any)?.tipo_servico?.preco_base) ??
    0;

  const precoHorarioSelecionado = useMemo(() => {
    if (!isHoraria) return undefined;

    const lista =
      Array.isArray((data as any)?.precos_detalhados)
        ? (data as any).precos_detalhados
        : Array.isArray((servico as any)?.precos_detalhados)
        ? (servico as any).precos_detalhados
        : [];

    const hi = horarioSelecionado?.hora_inicio ?? horaInicioParam;
    const hf = horarioSelecionado?.hora_fim ?? horaFimParam;

    const encontrado = lista.find((slot: any) => {
      const inicio = String(slot?.hora_inicio ?? "").slice(0, 5);
      const fim = String(slot?.hora_fim ?? "").slice(0, 5);
      return inicio === hi && fim === hf;
    });

    return toNumber(encontrado?.preco);
  }, [data, servico, horarioSelecionado, horaInicioParam, horaFimParam, isHoraria]);

  const dailyPrice = exigeModalidadeBanhoTosa
    ? (precoModalidadeSelecionada ?? menorPrecoModalidadeBanhoTosa ?? precoPadrao)
    : isHoraria
    ? (precoHorarioSelecionado ?? precoPadrao)
    : (
        toNumber((data as any)?.preco_exibicao) ??
        toNumber((servico as any)?.preco_exibicao) ??
        precoPadrao
      );

  const photos = useMemo(() => {
    const arr = Array.isArray(data.imagens) ? data.imagens : [];
    return Array.from(new Set(arr.filter(Boolean)));
  }, [data.imagens]);

  const mainPhoto = photos[0] ?? "https://placehold.co/1200x800?text=AllePets";
  const tempoResposta = data?.tempo_resposta ?? "—";

  const totalEstimado = useMemo(() => {
    if (!dailyPrice) return undefined;

    if (!temDataSelecionada) return undefined;

    if (isPeriodo) {
      const quantidadeDiarias = diffDays(entradaSelecionada, saidaSelecionada);
      return dailyPrice * quantidadeDiarias;
    }

    if (isHoraria) {
      const hi = horarioSelecionado?.hora_inicio ?? horaInicioParam;
      const hf = horarioSelecionado?.hora_fim ?? horaFimParam;
      const quantidadeHoras = diffHours(hi, hf);
      return dailyPrice * quantidadeHoras;
    }

    return dailyPrice;
  }, [
    dailyPrice,
    isPeriodo,
    isHoraria,
    entradaSelecionada,
    saidaSelecionada,
    horaInicioParam,
    horaFimParam,
    horarioSelecionado,
    temDataSelecionada,
  ]);

  const horariosDisponiveis = useMemo<HorarioSlot[]>(() => {
    if (!isHoraria) return [];

    const precosDetalhados =
      Array.isArray((data as any)?.precos_detalhados)
        ? (data as any).precos_detalhados
        : Array.isArray((servico as any)?.precos_detalhados)
        ? (servico as any).precos_detalhados
        : [];

    return precosDetalhados
      .filter((slot: any) => {
        return (
          slot &&
          slot.hora_inicio &&
          slot.hora_fim &&
          slot.status === "disponivel"
        );
      })
      .map((slot: any) => ({
        hora_inicio: String(slot.hora_inicio).slice(0, 5),
        hora_fim: String(slot.hora_fim).slice(0, 5),
      }));
  }, [isHoraria, data, servico]);

  useEffect(() => {
    if (!isHoraria || !horariosDisponiveis.length) return;

    const encontrado =
      horariosDisponiveis.find(
        (h) => h.hora_inicio === horaInicioParam && h.hora_fim === horaFimParam
      ) ?? null;

    if (encontrado) {
      setHorarioSelecionado(encontrado);
      return;
    }

    const horariosFiltrados = horariosDisponiveis.filter(
      (slot) => !horarioJaPassou(entradaSelecionada, slot.hora_inicio)
    );

    if (!horarioSelecionado && horariosFiltrados.length) {
      setHorarioSelecionado(horariosFiltrados[0]);
    }
  }, [
    isHoraria,
    horariosDisponiveis,
    horaInicioParam,
    horaFimParam,
    horarioSelecionado,
    entradaSelecionada,
  ]);

  const isFastResponse =
    typeof tempoResposta === "string" &&
    (tempoResposta.includes("min") ||
      tempoResposta.includes("5") ||
      tempoResposta.includes("10"));

  const respostaBg = isFastResponse ? COLORS.success : "#E5E7EB";
  const respostaColor = isFastResponse ? "#fff" : "#374151";

  const horaInicioAtual = horarioSelecionado?.hora_inicio ?? horaInicioParam;
  const horaFimAtual = horarioSelecionado?.hora_fim ?? horaFimParam;
  const quantidadeHorasAtual = diffHours(horaInicioAtual, horaFimAtual);

  const quantidadePetsSelecionados = petIdsSelecionados.length;
  const multiplicadorPets = quantidadePetsSelecionados > 0 ? quantidadePetsSelecionados : 1;
  const totalReservaComPets = selectedPackage
    ? selectedPackage.price * multiplicadorPets
    : totalEstimado != null
    ? totalEstimado * multiplicadorPets
    : undefined;
  const reservaBloqueadaPorHorario = Boolean(
    temDataSelecionada && isHoraria && !horarioSelecionado
  );
  const disabledButtonStyle = reservaBloqueadaPorHorario
    ? { backgroundColor: "#9CA3AF", color: "#fff" }
    : { backgroundColor: COLORS.primary, color: "#fff" };

  function openReservationForm(source: "desktop_sidebar" | "mobile_sticky") {
    trackFunnelEvent("reservation_form_open", {
      source,
      authenticated: Boolean(userId),
      has_date: temDataSelecionada,
      is_hourly: isHoraria,
      requires_variant: exigeModalidadeBanhoTosa,
    });
    setOpenReserva(true);
  }

  function trackAuthLinkClick(target: "login" | "register") {
    rememberAuthRedirect({
      source: "reservation_modal",
      target,
      page: "service_detail",
    });
  }

  const dataInicioPagamento = entradaSelecionada;
  const dataFimPagamento = saidaSelecionada || entradaSelecionada;

  const horaInicioPagamento =
    isHoraria
      ? `${horarioSelecionado?.hora_inicio ?? horaInicioParam ?? ""}:00`.replace("::00", ":00")
      : tipoPrecificacao === "creche" || tipoServicoSlug === "creche"
      ? "06:00:00"
      : "";

  const horaFimPagamento =
    isHoraria
      ? `${horarioSelecionado?.hora_fim ?? horaFimParam ?? ""}:00`.replace("::00", ":00")
      : tipoPrecificacao === "creche" || tipoServicoSlug === "creche"
      ? "18:00:00"
      : "";
      
  function togglePetSelecionado(petId: number) {
    setPetRequiredNotice(false);
    setPetIdsSelecionados((prev) =>
      prev.includes(petId) ? prev.filter((id) => id !== petId) : [...prev, petId]
    );
  }

  function iniciarListenerEcho(conversaId: number) {
    const channelName = `chat.${conversaId}`;

    try {
      if (chatConversaId) (echo as any).leave?.(`chat.${chatConversaId}`);
    } catch {}

    try {
      (echo as any)
        .private(channelName)
        .listen(".NovaMensagemEnviada", (data: any) => {
          if (Number(data?.conversa_id) !== Number(conversaId)) return;
          if (userId && Number(data?.remetente_id) === Number(userId)) return;

          const msg: ChatMsg = {
            id: data?.id,
            conversa_id: data?.conversa_id,
            remetente_id: data?.remetente_id,
            mensagem: data?.mensagem ?? data?.texto ?? "",
            created_at: data?.created_at ?? new Date().toISOString(),
          };

          setChatMensagens((prev) => [...prev, msg]);
        });
    } catch (e) {
      console.error("Erro ao iniciar listener Echo:", e);
    }
  }

  async function marcarComoLida(conversaId: number) {
    try {
      await fetch(`/chat/conversa/${conversaId}/marcar-lida`, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "X-CSRF-TOKEN": csrf,
        },
      });
    } catch {}
  }

  async function abrirChat() {
    if (!anfitriao?.id) return;

    if (chatOpen && chatAnfitriaoId && Number(chatAnfitriaoId) === Number(anfitriao.id)) {
      return;
    }

    setChatError(null);
    setChatLoading(true);
    setChatOpen(true);
    setChatTexto("");
    setChatAnfitriaoId(Number(anfitriao.id));
    setChatAnfitriaoNome(String(host?.nome ?? anfitriao?.name ?? "Anfitrião"));
    setChatMensagens([]);

    try {
      const res = await fetch(`/chat/get-conversa/${anfitriao.id}`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      if (res.status === 401) {
        rememberAuthRedirect({
          source: "chat",
          target: "login",
          page: "service_detail",
        });
        window.location.href = `/login?next=${encodeURIComponent(location.pathname + location.search)}`;
        return;
      }

      if (res.status === 403) {
        rememberAuthRedirect({
          source: "chat",
          target: "complete_profile",
          page: "service_detail",
        });
        window.location.href = `/completar-cadastro?next=${encodeURIComponent(location.pathname)}`;
        return;
      }

      if (!res.ok) throw new Error("Falha ao buscar a conversa.");

      const json = await res.json();
      const conversaId = Number(json?.conversa_id);
      if (!conversaId) throw new Error("conversa_id não retornou");

      setChatConversaId(conversaId);

      const msgs = Array.isArray(json?.mensagens) ? json.mensagens : [];
      setChatMensagens(
        msgs.map((m: any) => ({
          id: m?.id,
          conversa_id: conversaId,
          remetente_id: m?.remetente_id,
          mensagem: m?.mensagem ?? "",
          created_at: m?.created_at ?? new Date().toISOString(),
        }))
      );

      await marcarComoLida(conversaId);
      iniciarListenerEcho(conversaId);
    } catch (e) {
      console.error("Erro ao abrir o chat:", e);
      setChatError("Erro ao carregar o chat.");
    } finally {
      setChatLoading(false);
    }
  }

  function fecharChat() {
    setChatOpen(false);
    try {
      if (chatConversaId) (echo as any).leave?.(`chat.${chatConversaId}`);
    } catch {}
  }

  async function enviarMensagem(e?: React.FormEvent) {
    e?.preventDefault();

    const texto = chatTexto.trim();
    if (!texto || !chatConversaId) return;

    const optimistic: ChatMsg = {
      conversa_id: chatConversaId,
      remetente_id: userId ?? undefined,
      mensagem: texto,
      created_at: new Date().toISOString(),
    };

    setChatMensagens((prev) => [...prev, optimistic]);
    setChatTexto("");

    const formData = new FormData();
    formData.append("conversa_id", String(chatConversaId));
    formData.append("mensagem", texto);

    try {
      await fetch(`/chat/enviar`, {
        method: "POST",
        credentials: "include",
        body: formData,
        headers: {
          Accept: "application/json",
          "X-CSRF-TOKEN": csrf,
          "X-Socket-ID": (echo as any)?.socketId?.() ?? "",
        },
      });
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
    }
  }

  async function enviarParaPagamento() {
    if (!data) return;

    trackFunnelEvent("checkout_start", {
      authenticated: Boolean(userId),
      has_pet: petIdsSelecionados.length > 0,
      has_date: Boolean(dataInicioPagamento),
      is_hourly: isHoraria,
      requires_variant: exigeModalidadeBanhoTosa,
    });

    if (!petIdsSelecionados.length) {
      trackFunnelEvent("reservation_attempt_without_pet", {
        authenticated: Boolean(userId),
      });
      setPetRequiredNotice(true);
      return;
    }

    if (!dataInicioPagamento) {
      trackFunnelEvent("checkout_validation_error", {
        reason: "missing_date",
      });
      alert("Escolha a data da reserva antes de continuar.");
      return;
    }

    if (exigeModalidadeBanhoTosa && !modalidadeServico) {
      trackFunnelEvent("checkout_validation_error", {
        reason: "missing_service_variant",
      });
      alert("Escolha Só banho, Só tosa ou Banho e tosa para continuar.");
      return;
    }

    if (exigeModalidadeBanhoTosa && (!precoModalidadeSelecionada || precoModalidadeSelecionada <= 0)) {
      trackFunnelEvent("checkout_validation_error", {
        reason: "invalid_variant_price",
      });
      alert("A modalidade escolhida ainda não tem preço configurado.");
      return;
    }

    if (isHoraria && !horarioSelecionado) {
      trackFunnelEvent("checkout_validation_error", {
        reason: "missing_time_slot",
      });
      alert("Selecione um horário disponível para continuar.");
      return;
    }

    setPagamentoEnviando(true);

    try {
      const csrfToken =
        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || "";

      // Declaramos o preço unitário UMA ÚNICA VEZ aqui
    const precoUnitario =
      exigeModalidadeBanhoTosa
        ? Number(precoModalidadeSelecionada ?? 0)
        : isHoraria
        ? Number(precoHorarioSelecionado ?? precoPadrao ?? 0)
        : Number(precoPadrao ?? 0);
      
      if (precoUnitario <= 0) {
        trackFunnelEvent("checkout_validation_error", {
          reason: "invalid_price",
        });
        alert("Preço inválido para esta reserva.");
        return;
      }

      const payload = new URLSearchParams();
      payload.append("_token", csrfToken);
      payload.append("servico_id", String(data.servico_id ?? servico?.id ?? ""));
      payload.append("anfitriao_id", String(anfitriao?.id ?? ""));
      payload.append("data_inicio", dataInicioPagamento);
      payload.append("data_fim", dataFimPagamento);
      if (modalidadeServico) payload.append("modalidade_servico", modalidadeServico);
      if (selectedPackageId) payload.append("package_id", selectedPackageId);
      
      // Enviamos o preço UNITÁRIO, com 2 casas decimais
      payload.append("preco", precoUnitario.toFixed(2));
      
      // Enviamos o array de pets como string separada por vírgulas
      payload.append("pet_ids", petIdsSelecionados.join(","));
      
      payload.append("mensagem", "");

      // Horários (obrigatório para petsitter, passeio, etc.)
      if (horaInicioPagamento) payload.append("hora_inicio", horaInicioPagamento);
      if (horaFimPagamento) payload.append("hora_fim", horaFimPagamento);

      // Creche tem horário fixo
      if (tipoPrecificacao === "creche" || tipoServicoSlug === "creche") {
        payload.set("hora_inicio", "06:00:00");
        payload.set("hora_fim", "18:00:00");
      }

      console.log("DEBUG PAGAMENTO FETCH", Object.fromEntries(payload.entries()));

      const response = await fetch("/pagamento/criar", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          "X-CSRF-TOKEN": csrfToken,
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        body: payload.toString(),
      });

      const json = await response.json();
      console.log("PAGAMENTO RESPONSE JSON →", json);

      if (json?.errors) {
        console.error("ERROS DE VALIDAÇÃO DO LARAVEL:", json.errors);
        trackFunnelEvent("checkout_validation_error", {
          reason: "server_validation",
          error_count: Object.keys(json.errors).length,
        });
        const errorList = Object.entries(json.errors)
          .map(([campo, mensagens]) => `• ${campo}: ${Array.isArray(mensagens) ? mensagens.join(", ") : mensagens}`)
          .join("\n");

        alert(`Dados inválidos:\n\n${errorList}\n\nVerifique o console para mais detalhes.`);
        return;
      }

      if (json?.message === "O valor do serviço foi atualizado. Tente novamente.") {
        trackFunnelEvent("checkout_validation_error", {
          reason: "price_updated",
        });
        alert("⚠️ O preço foi atualizado. Recarregando página...");
        window.location.reload();
        return;
      }

      if (!response.ok) {
        trackFunnelEvent("checkout_validation_error", {
          reason: "checkout_request_failed",
          status: response.status,
        });
        alert(json?.message || "Não foi possível iniciar o pagamento.");
        return;
      }

      if (json?.checkout_url) {
        trackFunnelEvent("checkout_redirect_mp", {
          same_tab: true,
        });
        window.location.href = json.checkout_url;
        return;
      }

      trackFunnelEvent("checkout_validation_error", {
        reason: "missing_checkout_url",
      });
      alert("Pagamento criado, mas sem URL de checkout.");
    } catch (error) {
      console.error("ERRO AO ENVIAR PARA PAGAMENTO", error);
      trackFunnelEvent("checkout_validation_error", {
        reason: "network_error",
      });
      alert("Erro de conexão ao tentar criar o pagamento.");
    } finally {
      setPagamentoEnviando(false);
    }
  }

  if (loading) {
    return (
      <div className="p-10 text-center animate-pulse" style={{ color: COLORS.primary }}>
        Carregando detalhes...
      </div>
    );
  }

  if (err) {
    return (
      <div className="p-10 text-center" style={{ color: COLORS.primary }}>
        Erro: {err}
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen pb-20 md:pb-10 font-sans text-gray-900">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm font-medium hover:underline"
            type="button"
          >
            ← Voltar
          </button>
        </div>

        <header className="mb-6">
          <h1 className="text-2xl md:text-4xl font-bold tracking-tight mb-2">{title}</h1>

          <div className="flex flex-wrap items-center gap-3 text-sm font-medium">
            {media != null ? (
              <span className="flex items-center gap-2">
                <span style={{ color: COLORS.success }}>⭐ {media.toFixed(1)}</span>
                <button
                  type="button"
                  className="underline font-medium"
                  onClick={() => setOpenReviews(true)}
                >
                  {avals.length} avaliações
                </button>
              </span>
            ) : (
              <span className="opacity-70">Sem avaliações ainda</span>
            )}

            <span className="text-gray-400">|</span>

            <span className="underline decoration-1 underline-offset-2">
              {hra?.cidade ?? "Localização não informada"}
              {hra?.estado ? `, ${hra.estado}` : ""}
            </span>

            <span className="text-gray-400">|</span>

            <span className="opacity-80">
              <strong>Serviço:</strong> {tipoServicoNome}
            </span>
          </div>
        </header>

        <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[300px] md:h-[450px] overflow-hidden rounded-2xl mb-8">
          <div className="col-span-4 md:col-span-2 row-span-2 bg-muted flex items-center justify-center overflow-hidden rounded-l-2xl">
            <img
              src={mainPhoto}
              className="w-full h-full object-contain"
              alt="Foto principal"
            />
          </div>

          <div className="hidden md:flex col-span-1 row-span-1 bg-muted items-center justify-center overflow-hidden">
            <img
              src={photos[1] ?? mainPhoto}
              className="w-full h-full object-contain"
              alt="Foto 2"
            />
          </div>

          <div className="hidden md:flex col-span-1 row-span-1 bg-muted items-center justify-center overflow-hidden rounded-tr-2xl">
            <img
              src={photos[2] ?? mainPhoto}
              className="w-full h-full object-contain"
              alt="Foto 3"
            />
          </div>

          <div className="hidden md:flex col-span-1 row-span-1 bg-muted items-center justify-center overflow-hidden">
            <img
              src={photos[3] ?? mainPhoto}
              className="w-full h-full object-contain"
              alt="Foto 4"
            />
          </div>

          <div className="hidden md:flex col-span-1 row-span-1 bg-muted items-center justify-center overflow-hidden rounded-br-2xl">
            <img
              src={photos[4] ?? mainPhoto}
              className="w-full h-full object-contain"
              alt="Foto 5"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="md:col-span-2 space-y-8">
            <section className="space-y-4 border-b pb-8">
              <h2 className="text-xl font-bold">{host?.nome ?? anfitriao?.name ?? "Anfitrião"}</h2>

              <div className="text-sm text-gray-700">
                <strong>Bio:</strong> {bio}
              </div>

              <div className="text-sm text-gray-700">
                <strong>Serviço:</strong> {tipoServicoNome}
              </div>

              <div className="text-sm text-gray-700">
                <strong>Endereço:</strong> {endereco}
              </div>

              {limitePets != null && (
                <div className="text-sm text-gray-700">
                  <strong>🐱 Aceita até:</strong> {limitePets} pet(s) simultaneamente
                </div>
              )}

              <div className="text-sm text-gray-700">
                <strong>Avaliação:</strong>{" "}
                {media != null ? (
                  <button
                    type="button"
                    onClick={() => setOpenReviews(true)}
                    className="ml-2 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/30 bg-[#fff8df] px-3 py-1 text-sm font-semibold text-gray-900 transition hover:bg-[#fff2bd]"
                  >
                    <RatingStars value={media} />
                    <span>{Number(media).toFixed(1)}</span>
                    <span className="text-gray-500">
                      ({totalAvaliacoes})
                    </span>
                  </button>
                ) : (
                  <span className="opacity-70">
                    Este anfitrião ainda não recebeu avaliações.
                  </span>
                )}
              </div>

              {(demandBadge || watchingNow != null) && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {showUrgency && (
                    <div className="mt-2">
                      <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full bg-gray-50 text-gray-700 border">
                        {urgencyText}
                      </span>
                    </div>
                  )}

                  {watchingNow != null && (
                    <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full bg-gray-50 text-gray-700 border">
                      👀 {watchingNow} visualizações agora
                    </span>
                  )}
                </div>
              )}

              <div className="text-sm text-gray-700">
                <strong>Aceita:</strong>{" "}
                {aceitaBadges.length ? (
                  <span className="ml-2">{aceitaBadges.join(" · ")}</span>
                ) : (
                  <span className="opacity-70 ml-2">Não informado</span>
                )}
              </div>

              <div className="text-sm text-gray-700">
                <strong>Porte:</strong>{" "}
                {porteBadges.length ? (
                  <span className="ml-2">
                    {porteBadges.map((p) => (
                      <span
                        key={p}
                        className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs mr-2"
                      >
                        {p}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="opacity-70 ml-2">Não informado</span>
                )}
              </div>

              <div className="text-sm text-gray-700">
                <strong>Serviços adicionais:</strong>{" "}
                {adicionaisBadges.length ? (
                  <span className="ml-2">
                    {adicionaisBadges.map((a) => (
                      <span
                        key={a}
                        className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs mr-2"
                      >
                        {a}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="opacity-70 ml-2">Não informado</span>
                )}
              </div>

              <div className="text-sm text-gray-700 flex items-center gap-2 flex-wrap">
                <strong>Política de cancelamento:</strong>

                <span
                  className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-bold"
                  style={{
                    backgroundColor: policyBadgeLabel.includes("🟢")
                      ? COLORS.successLight
                      : COLORS.accent,
                    color: policyBadgeLabel.includes("🟢") ? COLORS.success : COLORS.primary,
                  }}
                >
                  {policyBadgeLabel}
                </span>

                <HoverPopover
                  content={
                    <CancellationPopoverContent
                      bullets={cancelWin.bullets}
                      inicio={cancelWin.inicio}
                      limite100Label={cancelWin.limite100Label}
                    />
                  }
                />

                <div className="w-full text-xs text-gray-500 mt-1">{policyShort}</div>
              </div>

              {mostrarBlocoVagas && (
                <div className="w-full border rounded-xl divide-y">
                  <div className="p-3 text-xs uppercase font-bold text-gray-500">Vagas</div>
                  <div className="p-3 text-sm">
                    {estaLotado ? (
                      <span className="font-bold text-red-600">🔴 Sem vagas disponíveis</span>
                    ) : (
                      <>
                        <span className="font-bold" style={{ color: COLORS.success }}>
                          {ultimaVaga ? "Última vaga disponível" : `${vagasDisp} vagas disponíveis`}
                        </span>{" "}
                        no período
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg border text-sm font-medium"
                  style={{ borderColor: COLORS.primary, color: COLORS.primary }}
                  onClick={abrirChat}
                >
                  💬 Quer conversar agora? Estou aqui para te ajudar!
                </button>

                <span
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold"
                  style={{
                    backgroundColor: respostaBg,
                    color: respostaColor,
                  }}
                >
                  ⚡ {isFastResponse ? "Resposta rápida:" : "Tempo de resposta:"} {tempoResposta}
                </span>
              </div>
            </section>

            <DetailSections
              descricaoServico={descricaoServico}
              adicionaisBadges={adicionaisBadges}
              policyBadgeLabel={policyBadgeLabel}
              cancellationText={cancellationText}
              media={media}
              avals={avals}
              onOpenReviews={() => setOpenReviews(true)}
            />
          </div>

          <aside className="hidden md:block">
            <div className="sticky top-6 border rounded-2xl p-6 shadow-xl space-y-4 bg-white">
              <div className="flex justify-between items-start">
                {exigeModalidadeBanhoTosa ? (
                  <div className="space-y-1 text-sm text-gray-700">
                    {modalidadesDisponiveisBanhoTosa.map((opcao) => (
                      <div key={opcao.key} className="flex gap-2">
                        <span className="font-semibold">{opcao.label}:</span>
                        <span>{brl(precosModalidadeBanhoTosa[opcao.key])}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <span className="text-2xl font-bold" style={{ color: COLORS.primary }}>
                      {brl(dailyPrice)}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {isHoraria ? " / hora" : isPeriodo ? " / período" : ""}
                    </span>
                  </div>
                )}

                {media != null && <span className="text-sm font-semibold">⭐ {media.toFixed(1)}</span>}
              </div>

              {mostrarBlocoVagas && (
                <div className="w-full border rounded-xl divide-y">
                  <div className="p-3 text-xs uppercase font-bold text-gray-500">Vagas</div>
                  <div className="p-3 text-sm">
                    {estaLotado ? (
                      <span className="font-bold text-red-600">🔴 Sem vagas disponíveis</span>
                    ) : (
                      <>
                        <span className="font-bold" style={{ color: COLORS.success }}>
                          {ultimaVaga ? "Última vaga disponível" : `${vagasDisp} vagas disponíveis`}
                        </span>{" "}
                        no período
                      </>
                    )}
                  </div>
                </div>
              )}

              {isHoraria && (
                <div className="space-y-3">
                  <div className="text-sm font-semibold text-gray-800">Horários disponíveis</div>

                  <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto pr-1">
                    {horariosDisponiveis
                      .filter((slot) => !horarioJaPassou(entradaSelecionada, slot.hora_inicio))
                      .map((slot, idx) => {
                      const ativo =
                        horarioSelecionado?.hora_inicio === slot.hora_inicio &&
                        horarioSelecionado?.hora_fim === slot.hora_fim;

                      return (
                        <button
                          key={`${slot.hora_inicio}-${slot.hora_fim}-${idx}`}
                          type="button"
                          onClick={() => setHorarioSelecionado(slot)}
                          className={`w-full rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                            ativo
                              ? "text-white border-transparent shadow-sm"
                              : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                          }`}
                          style={ativo ? { backgroundColor: COLORS.primary } : {}}
                        >
                          {slot.hora_inicio} às {slot.hora_fim}
                        </button>
                      );
                    })}
                  </div>

                  {horarioSelecionado && (
                    <div className="rounded-lg bg-gray-50 border px-3 py-2 text-sm text-gray-700">
                      Selecionado: <strong>{horarioSelecionado.hora_inicio}</strong> às{" "}
                      <strong>{horarioSelecionado.hora_fim}</strong>
                    </div>
                  )}
                </div>
              )}

              <button
                type="button"
                disabled={reservaBloqueadaPorHorario}
                aria-disabled={reservaBloqueadaPorHorario}
                className="w-full text-white font-bold py-3 rounded-xl transition-all shadow-md active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100"
                style={disabledButtonStyle}
                onClick={() => openReservationForm("desktop_sidebar")}
              >
                {reservaBloqueadaPorHorario ? "Escolha um horário" : "Solicitar Reserva"}
              </button>
              <div className="border-t pt-4" />
            </div>
          </aside>
        </div>
      </div>

      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t shadow-lg">
  {isHoraria && (
    <div className="border-b px-4 py-3">
      <div className="space-y-3">
        <div className="text-sm font-semibold text-gray-800">Horários disponíveis</div>

        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
          {horariosDisponiveis
            .filter((slot) => !horarioJaPassou(entradaSelecionada, slot.hora_inicio))
            .map((slot, idx) => {
              const ativo =
                horarioSelecionado?.hora_inicio === slot.hora_inicio &&
                horarioSelecionado?.hora_fim === slot.hora_fim;

              return (
                <button
                  key={`${slot.hora_inicio}-${slot.hora_fim}-${idx}`}
                  type="button"
                  onClick={() => setHorarioSelecionado(slot)}
                  className={`w-full rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                    ativo
                      ? "text-white border-transparent shadow-sm"
                      : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                  }`}
                  style={ativo ? { backgroundColor: COLORS.primary } : {}}
                >
                  {slot.hora_inicio} às {slot.hora_fim}
                </button>
              );
            })}
        </div>

        {horarioSelecionado && (
          <div className="rounded-lg bg-gray-50 border px-3 py-2 text-sm text-gray-700">
            Selecionado: <strong>{horarioSelecionado.hora_inicio}</strong> às{" "}
            <strong>{horarioSelecionado.hora_fim}</strong>
          </div>
        )}
      </div>
    </div>
  )}

  <div className="px-6 py-3 flex items-center justify-between">
    <div>
      {!exigeModalidadeBanhoTosa && (
        <div className="font-black text-lg" style={{ color: COLORS.primary }}>
          {brl(totalEstimado)}
          <span className="text-sm font-normal text-gray-500">
            {isHoraria ? " total" : isPeriodo ? " total" : ""}
          </span>
        </div>
      )}

      <div className="text-xs font-semibold text-gray-700">
        {exigeModalidadeBanhoTosa ? (
          <div className="space-y-0.5">
            {modalidadesDisponiveisBanhoTosa.map((opcao) => (
              <div key={opcao.key}>{opcao.label}: {brl(precosModalidadeBanhoTosa[opcao.key])}</div>
            ))}
          </div>
        ) : isHoraria ? (
          `${brl(dailyPrice)} por hora · ${quantidadeHorasAtual}h`
        ) : isPeriodo ? (
          temDataSelecionada
            ? `${brl(dailyPrice)} por período · ${diffDays(entradaSelecionada, saidaSelecionada)} período(s)`
            : "Escolha a data"
        ) : urgencyText ? (
          urgencyText
        ) : media != null ? (
          `⭐ ${media.toFixed(1)}`
        ) : (
          "Novo"
        )}
      </div>
    </div>

    <button
      type="button"
      disabled={reservaBloqueadaPorHorario}
      aria-disabled={reservaBloqueadaPorHorario}
      className="px-6 py-3 rounded-xl font-bold shadow-md active:scale-95 transition-transform disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100"
      style={disabledButtonStyle}
      onClick={() => openReservationForm("mobile_sticky")}
    >
      {reservaBloqueadaPorHorario ? "Escolha horário" : "Reservar"}
    </button>
  </div>
</div>

      {openReserva && (
        <div
          className="fixed inset-0 z-[999] bg-black/50 flex items-center justify-center p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpenReserva(false);
          }}
        >
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="font-bold text-lg">Solicitar Reserva</div>
              <button
                type="button"
                className="px-3 py-1 rounded-lg border text-sm font-semibold"
                onClick={() => setOpenReserva(false)}
              >
                Fechar
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="text-sm text-gray-700">
                <strong>Serviço:</strong> {tipoServicoNome}
              </div>

              <div className="text-sm text-gray-700">
                <strong>Anfitrião:</strong> {host?.nome ?? anfitriao?.name ?? "Anfitrião"}
              </div>

              {mostrarAvisoCadastro && (
                <div className="rounded-xl border border-pink-200 bg-pink-50 px-4 py-3 text-sm text-pink-900">
                  <div className="font-bold">
                    {acessoDiretoSemData
                      ? "Veio pelo QR Code?"
                      : "Cadastro necessário"}
                  </div>
                  <p className="mt-1">
                    Para acessar os serviços da AllePets, escolher seus pets e continuar a reserva,
                    é preciso criar uma conta ou entrar com seu cadastro.
                  </p>
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <a
                      href={cadastroUrl}
                      onClick={() => trackAuthLinkClick("register")}
                      className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-bold text-white"
                      style={{ backgroundColor: COLORS.primary }}
                    >
                      Criar cadastro
                    </a>
                    <a
                      href={loginUrl}
                      onClick={() => trackAuthLinkClick("login")}
                      className="inline-flex items-center justify-center rounded-xl border border-pink-200 bg-white px-4 py-2 text-sm font-bold text-pink-700"
                    >
                      Já tenho cadastro
                    </a>
                  </div>
                </div>
              )}

              <div className={isPeriodo ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : "grid grid-cols-1 gap-3"}>
                <label className="block">
                  <span className="block text-sm font-semibold text-gray-800 mb-1">
                    {isPeriodo ? "Data de entrada" : "Data da reserva"}
                  </span>
                  <input
                    type="date"
                    min={hojeYmd}
                    value={entradaSelecionada}
                    onChange={(e) => {
                      const novaEntrada = e.target.value;
                      setReservaEntrada(novaEntrada);
                      setReservaSaida((atual) => {
                        if (!isPeriodo) return novaEntrada;
                        if (!atual || atual < novaEntrada) return novaEntrada;
                        return atual;
                      });
                      setHorarioSelecionado(null);
                    }}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
                    required
                  />
                </label>

                {isPeriodo && (
                  <label className="block">
                    <span className="block text-sm font-semibold text-gray-800 mb-1">
                      Data de saída
                    </span>
                    <input
                      type="date"
                      min={entradaSelecionada || hojeYmd}
                      value={saidaSelecionada}
                      onChange={(e) => setReservaSaida(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
                      required
                    />
                  </label>
                )}
              </div>

              {isPeriodo && (
                <div className="rounded-xl border border-rose-100 bg-rose-50/70 px-4 py-3 text-sm text-gray-800">
                  <div className="font-semibold text-gray-900">Check-in e check-out</div>
                  <div className="mt-1">
                    Check-in a partir de <strong>{checkinPadrao}</strong> e check-out até <strong>{checkoutPadrao}</strong>.
                  </div>
                  {resumoCheckout && (
                    <div className="mt-2 text-gray-700">
                      {resumoCheckout}
                    </div>
                  )}
                </div>
              )}

              {!temDataSelecionada && (
                <div className="rounded-xl border border-pink-200 bg-pink-50 px-3 py-2 text-sm text-pink-800">
                  Escolha a data para ver disponibilidade e continuar para o pagamento.
                </div>
              )}

              {inAppBrowser && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Você está navegando pelo Instagram/Facebook. Se o pagamento não abrir corretamente,
                  use a opção de abrir no navegador do celular. Você pode continuar por aqui.
                </div>
              )}

              {exigeModalidadeBanhoTosa && (
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-gray-800">
                    Escolha o serviço
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {modalidadesDisponiveisBanhoTosa.map((opcao) => {
                      const preco = precosModalidadeBanhoTosa[opcao.key];
                      const ativo = modalidadeServico === opcao.key;

                      return (
                        <button
                          key={opcao.key}
                          type="button"
                          onClick={() => {
                            setModalidadeServico(opcao.key);
                            if (selectedPackage?.variant_key && selectedPackage.variant_key !== opcao.key) {
                              setSelectedPackageId("");
                            }
                          }}
                          className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                            ativo
                              ? "border-transparent text-white shadow-sm"
                              : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                          }`}
                          style={ativo ? { backgroundColor: COLORS.primary } : {}}
                        >
                          <span className="block font-bold">{opcao.label}</span>
                          <span className={ativo ? "text-white/90" : "text-gray-500"}>
                            {brl(preco)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {servicePackages.length > 0 && (
                <div className="space-y-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Como você quer reservar?</div>
                    <p className="text-xs text-gray-500">Escolha uma reserva avulsa ou compre um pacote pré-pago.</p>
                  </div>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setSelectedPackageId("")}
                      className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                        selectedPackageId === "" ? "border-pink-400 bg-pink-50" : "border-gray-300 bg-white"
                      }`}
                    >
                      <span className="block text-sm font-bold">Reserva avulsa</span>
                      <span className="text-xs text-gray-500">Pague apenas por esta reserva</span>
                    </button>
                    {servicePackages.map((pkg) => {
                      const active = selectedPackageId === pkg.id;
                      const variantLabel = bathPackageLabel(pkg.variant_key);
                      return (
                        <button
                          key={pkg.id}
                          type="button"
                          onClick={() => {
                            setSelectedPackageId(pkg.id);
                            if (pkg.variant_key) setModalidadeServico(pkg.variant_key);
                          }}
                          className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                            active ? "border-pink-400 bg-pink-50" : "border-gray-300 bg-white"
                          }`}
                        >
                          <span className="flex items-start justify-between gap-3">
                            <span>
                              <span className="block text-sm font-bold">{pkg.name || `Pacote com ${pkg.units} utilizações`}</span>
                              <span className="block text-xs text-gray-500">
                                {pkg.units} utilizações · válido por {packageValidityLabel(pkg)}
                                {variantLabel ? ` · ${variantLabel}` : ""}
                              </span>
                            </span>
                            <strong className="whitespace-nowrap text-sm" style={{ color: COLORS.success }}>{brl(pkg.price)}</strong>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500">O valor e a validade escolhidos serão registrados na reserva. O total final é recalculado pela AllePets.</p>
                </div>
              )}

              {isHoraria && temDataSelecionada && (
                <div className="space-y-3">
                  <div className="text-sm font-semibold text-gray-800">Horários disponíveis</div>

                  {horariosDisponiveis.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                      {horariosDisponiveis
                        .filter((slot) => !horarioJaPassou(entradaSelecionada, slot.hora_inicio))
                        .map((slot, idx) => {
                          const ativo =
                            horarioSelecionado?.hora_inicio === slot.hora_inicio &&
                            horarioSelecionado?.hora_fim === slot.hora_fim;

                          return (
                            <button
                              key={`${slot.hora_inicio}-${slot.hora_fim}-modal-${idx}`}
                              type="button"
                              onClick={() => setHorarioSelecionado(slot)}
                              className={`w-full rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                                ativo
                                  ? "text-white border-transparent shadow-sm"
                                  : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                              }`}
                              style={ativo ? { backgroundColor: COLORS.primary } : {}}
                            >
                              {slot.hora_inicio} às {slot.hora_fim}
                            </button>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Nenhum horário disponível para esta data.
                    </div>
                  )}

                  {horarioSelecionado && (
                    <div className="rounded-lg bg-gray-50 border px-3 py-2 text-sm text-gray-700">
                      Selecionado: <strong>{horarioSelecionado.hora_inicio}</strong> às{" "}
                      <strong>{horarioSelecionado.hora_fim}</strong>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Selecione seu(s) pet(s)
                </label>

                {!userId ? (
                  <div className="text-sm text-gray-500">
                    Após entrar ou criar seu cadastro, seus pets aparecerão aqui para concluir a reserva.
                  </div>
                ) : petsLoading ? (
                  <div className="text-sm text-gray-500">Carregando pets...</div>
                ) : meusPets.length > 0 ? (
                  <div className="space-y-2">
                    {meusPets.map((pet) => (
                      <label key={pet.id} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={petIdsSelecionados.includes(Number(pet.id))}
                          onChange={() => togglePetSelecionado(Number(pet.id))}
                          className="rounded border-gray-300"
                        />
                        <span>🐾 {pet.nome}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <div className="font-bold">Cadastre um pet para continuar</div>
                    <p className="mt-1">
                      Para reservar este serviço, primeiro cadastre o pet que receberá o cuidado.
                      Depois do cadastro, você volta para esta reserva.
                    </p>
                    <a
                      href={cadastrarPetUrl}
                      className="mt-3 inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-bold text-white"
                      style={{ backgroundColor: COLORS.primary }}
                    >
                      Cadastrar pet
                    </a>
                  </div>
                )}

                {petsError && <div className="text-sm text-red-600 mt-2">{petsError}</div>}
                {petRequiredNotice && meusPets.length > 0 && petIdsSelecionados.length === 0 && (
                  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    Selecione pelo menos um pet para continuar com a reserva.
                  </div>
                )}
              </div>

              <div className="flex justify-between font-bold text-gray-800 border-t pt-4">
                <span>Total estimado</span>
                <span>
                  {!temDataSelecionada
                    ? "Escolha a data"
                    : exigeModalidadeBanhoTosa && !modalidadeServico
                    ? "Escolha o serviço"
                    : brl(totalReservaComPets)}
                </span>
              </div>

              <button
                  type="button"
                  className="w-full text-white font-bold py-3 rounded-xl disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
                  style={
                    !temDataSelecionada ||
                    (exigeModalidadeBanhoTosa && (!modalidadeServico || !precoModalidadeSelecionada)) ||
                    (isHoraria && !horarioSelecionado) ||
                    pagamentoEnviando
                      ? { backgroundColor: "#9CA3AF" }
                      : { backgroundColor: COLORS.primary }
                  }
                  disabled={
                    !temDataSelecionada ||
                    (exigeModalidadeBanhoTosa && (!modalidadeServico || !precoModalidadeSelecionada)) ||
                    (isHoraria && !horarioSelecionado) ||
                    pagamentoEnviando
                  }
                  onClick={enviarParaPagamento}
                >
                  {pagamentoEnviando ? (
                    <>
                      <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      Aguarde, estamos encaminhando você para o pagamento...
                    </>
                  ) : (
                    "Confirmar e Ir para Pagamento"
                  )}
                </button>
            </div>
          </div>
        </div>
      )}

      {openReviews && (
        <div
          className="fixed inset-0 z-[999] bg-black/50 flex items-center justify-center p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpenReviews(false);
          }}
        >
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <div className="font-bold text-lg">
                  Avaliações de {host?.nome ?? anfitriao?.nome ?? anfitriao?.name ?? "anfitrião"}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  Histórico de feedbacks enviados por tutores após reservas concluídas.
                </div>
              </div>
              <button
                type="button"
                className="px-3 py-1 rounded-lg border text-sm font-semibold"
                onClick={() => setOpenReviews(false)}
              >
                Fechar
              </button>
            </div>

            <div className="max-h-[70vh] overflow-auto p-4 space-y-3">
              <div className="rounded-2xl bg-[#f1f7e6] p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl text-[#d4af37]">★</span>
                      <span className="text-3xl font-extrabold text-gray-900">
                        {media != null ? Number(media).toFixed(1) : "0.0"}
                      </span>
                    </div>
                    <p className="mt-1 text-sm font-semibold text-[#4f7d02]">
                      {totalAvaliacoes} avaliação{totalAvaliacoes === 1 ? "" : "es"} recebida{totalAvaliacoes === 1 ? "" : "s"}
                    </p>
                  </div>
                  <RatingStars value={media ?? 0} />
                </div>
              </div>

              {avals.length ? (
                avals.map((a: any, idx: number) => {
                  const nota = Number(a?.nota ?? 0);
                  return (
                    <div
                      key={a?.id ?? `${a?.user_id ?? "u"}-${a?.created_at ?? idx}`}
                      className="border border-gray-100 bg-gray-50 rounded-xl p-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-bold text-sm inline-flex items-center gap-2">
                          <RatingStars value={nota} />
                          <span className="text-gray-500 font-medium">({nota || 0}/5)</span>
                        </div>
                        <div className="text-xs text-gray-500">{formatDateBR(a?.data ?? a?.created_at)}</div>
                      </div>

                      <div className="mt-2 text-sm text-gray-700">
                        <strong>{getTutorName(a)}</strong>
                        {(a?.servico_nome || a?.reserva?.servico?.tipo_servico?.nome) ? (
                          <span className="text-gray-500">
                            {" "}
                            • {a?.servico_nome ?? a?.reserva?.servico?.tipo_servico?.nome}
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-2 text-sm text-gray-600 italic whitespace-pre-line">
                        “{safeComment(a)}”
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-sm text-gray-500">Ainda não há avaliações.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {chatOpen && (
        <div className="fixed bottom-0 right-4 z-[1050] w-[370px] h-[500px] border border-gray-300 bg-white rounded-t-xl shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
            <strong className="text-sm" id="chat-nome-anfitriao">
              Chat com {chatAnfitriaoNome}
            </strong>
            <button type="button" onClick={fecharChat} className="text-xl leading-none px-2">
              ×
            </button>
          </div>

          <div className="flex-1 overflow-auto p-3 space-y-2" id="chat-mensagens">
            {chatLoading && <div className="text-center text-gray-500 text-sm">Carregando...</div>}
            {chatError && <div className="text-center text-red-600 text-sm">{chatError}</div>}

            {!chatLoading &&
              !chatError &&
              chatMensagens.map((m, idx) => {
                const key = m.id ?? `${m.created_at}-${idx}`;
                const dayKey = diaKeyFromISO(m.created_at);
                const showSep =
                  dayKey &&
                  dayKey !== (idx === 0 ? null : diaKeyFromISO(chatMensagens[idx - 1]?.created_at));

                const minha = userId && Number(m.remetente_id) === Number(userId);

                return (
                  <React.Fragment key={key}>
                    {showSep && (
                      <div className="text-center text-xs text-gray-500 my-2">
                        {labelSeparador(m.created_at)}
                      </div>
                    )}

                    <div className={`flex ${minha ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-sm border ${
                          minha ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div className="whitespace-pre-wrap text-gray-800">{m.mensagem}</div>
                        <div className="text-[11px] text-gray-500 mt-1 text-right">
                          {horaHHmm(m.created_at)}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
          </div>

          <form onSubmit={enviarMensagem} className="border-t p-2 flex gap-2">
            <input
              type="text"
              className="flex-1 border rounded-lg px-3 py-2 text-sm"
              placeholder="Digite uma mensagem..."
              value={chatTexto}
              onChange={(e) => setChatTexto(e.target.value)}
              disabled={chatLoading || !chatConversaId}
              autoComplete="off"
            />
            <button
              type="submit"
              className="px-3 py-2 rounded-lg text-white text-sm font-semibold"
              style={{ backgroundColor: COLORS.primary }}
              disabled={chatLoading || !chatConversaId}
            >
              Enviar
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
